import { isEmpty } from 'lodash';

export function generateGridData(data, coreGroupsByWells) {
    if (!data) return { columnsDef: [], rowData: [] };

    const rowData = [];
    const columnsDef = [];

    const fields = new Set(); // Для хранения уникальных полей

    // Вспомогательная функция для добавления вложенных полей
    function addNestedFields(fieldKey, fieldValue) {
        if (typeof fieldValue === 'object' && fieldValue !== null) {
            // Если это объект, обрабатываем вложенные поля
            Object.entries(fieldValue).forEach(([subFieldKey]) => {
                fields.add(`${fieldKey}.${subFieldKey}`); // Добавляем в формате "группа.поле"
            });
        } else {
            fields.add(fieldKey); // Добавляем обычное поле
        }
    }

    // Проверка на существование поля в columnsDef
    function isFieldExists(field) {
        return columnsDef.some(
            col => col.field === field || (col.children && col.children.some(child => child.field === field)),
        );
    }

    const isEmptyOrWhitespace = value => {
        return value === undefined || value === null || value === '';
    };

    // Функция для расчёта rowSpan
    const calculateRowSpan = (params, field) => {
        const { api, node } = params;
        const currentValue = params.data[field];

        // Пропускаем пустые или недопустимые значения для объединения
        if (!currentValue || isEmptyOrWhitespace(currentValue)) {
            return 1;
        }

        let rowSpan = 1;
        let rowIndex = node.rowIndex + 1;
        const rowCount = api.getDisplayedRowCount();

        // Проверяем строки ниже на наличие таких же значений
        while (rowIndex < rowCount) {
            const nextRow = api.getDisplayedRowAtIndex(rowIndex);

            // Если следующая строка отсутствует, прерываем цикл
            if (!nextRow || !nextRow.data) {
                break;
            }

            const nextValue = nextRow.data[field];

            // Увеличиваем rowSpan, если значения совпадают
            if (nextValue === currentValue && !isEmptyOrWhitespace(nextValue)) {
                rowSpan++;
            } else {
                break;
            }

            rowIndex++;
        }

        return rowSpan;
    };

    // Для обработки вложенных объектов создаем вспомогательную функцию
    function createColumnGroup(groupName, subFields) {
        return {
            headerName: groupName,
            children: subFields.map(subField => ({
                headerName: subField,
                field: subField,
                cellClass: `flex items-center justify-end bg-white`,
            })),
        };
    }
    let row = null;
    const coreTypesTitles = [];
    // Проходим по каждому интервалу
    Object.entries(data).forEach(([intervalKey, intervalData]) => {
        // Проходим по каждому образцу внутри интервала
        Object.entries(intervalData.samples).forEach(([sampleKey, sampleData]) => {
            row = {
                interval: intervalKey,
                sampleNumber: sampleKey,
                ...intervalData,
                ...sampleData,
            };
            if (sampleData.coreTypes) {
                Object.entries(sampleData.coreTypes).forEach(([coreType, coreTypeValue]) => {
                    const coreTypeTitle = coreTypeValue.unit
                        ? `${coreTypeValue.title},${coreTypeValue.unit}`
                        : coreTypeValue.title;
                    if (!coreTypesTitles.includes(coreTypeTitle)) coreTypesTitles.push(coreTypeTitle);

                    row[coreTypeTitle] = coreTypeValue.value;
                });
            }

            rowData.push(row);
        });
    });

    if (Object.keys(coreGroupsByWells).length > 0) {
        const typeOrder = Object.values(coreGroupsByWells).flatMap(group =>
            Object.values(group.types).map(type => type.typeTitle),
        );
        coreTypesTitles.sort((a, b) => {
            const indexA = typeOrder.indexOf(a);
            const indexB = typeOrder.indexOf(b);
            if (indexA === -1 && indexB === -1) return 0;
            if (indexA === -1) return 1;
            if (indexB === -1) return -1;
            return indexA - indexB;
        });
    }

    if (!isFieldExists('recoveryDate')) {
        columnsDef.push({
            headerName: 'Дата отбора',
            field: 'recoveryDate',
            valueFormatter: params => {
                if (typeof params.value === 'number') {
                    return params.value.toFixed(1);
                }
                if (isEmpty(params.value)) {
                    return '';
                }
                if (params.value === 'N/A') {
                    return '1';
                }
                return params.value;
            },
        });
    }

    if (!isFieldExists('roof')) {
        columnsDef.push({
            headerName: 'Кровля интервала',
            field: 'roof',
            headerClass: 'text-right',
            valueFormatter: params => {
                if (typeof params.value === 'number') {
                    return params.value.toFixed(1);
                }
                if (isEmpty(params.value)) {
                    return 0;
                }
                return params.value;
            },
        });
    }

    if (!isFieldExists('bottom')) {
        columnsDef.push({
            headerName: 'Подошва интервала',
            field: 'bottom',
            headerClass: 'text-right',
            valueFormatter: params => {
                if (typeof params.value === 'number') {
                    return params.value.toFixed(1);
                }
                if (isEmpty(params.value)) {
                    return 0;
                }
                return params.value;
            },
        });
    }

    if (!isFieldExists('takeOut')) {
        columnsDef.push({
            headerName: 'Вынос керна м',
            field: 'takeOut',
            valueFormatter: params => {
                if (typeof params.value === 'number') {
                    return params.value.toFixed(1);
                }
                if (isEmpty(params.value)) {
                    return '0';
                }
                return params.value;
            },
        });
    }

    if (!isFieldExists('showSample')) {
        columnsDef.push({
            headerName: 'Тип проявления',
            field: 'showSample',
            valueFormatter: params => {
                if (typeof params.value === 'number') {
                    return params.value.toFixed(1);
                }
                if (isEmpty(params.value)) {
                    return '';
                }
                return params.value;
            },
        });
    }

    if (!isFieldExists('shapeSample')) {
        columnsDef.push({
            headerName: 'Вид',
            field: 'shapeSample',
        });
    }
    if (!isFieldExists('stratigr')) {
        columnsDef.push({
            headerName: 'Стратиграфия',
            field: 'stratigr',
        });
    }
    if (!isFieldExists('stratigraphyTitle')) {
        columnsDef.push({
            headerName: 'Пласт',
            field: 'stratigraphyTitle',
        });
    }
    if (!isFieldExists('intervalsGis')) {
        columnsDef.push({
            headerName: 'Интервалы-ГИС',
            field: 'intervalsGis',
        });
    }
    if (!isFieldExists('roofPeret')) {
        columnsDef.push({
            headerName: 'Кровля инт-ла отбора после перетяжки м',
            field: 'roofPeret',
            valueFormatter: params => {
                if (typeof params.value === 'number') {
                    return params.value.toFixed(1);
                }
                if (isEmpty(params.value)) {
                    return '';
                }
                return params.value;
            },
        });
    }
    if (!isFieldExists('bottomPeret')) {
        columnsDef.push({
            headerName: 'Подошва инт-ла отбора после перетяжки м',
            field: 'bottomPeret',
            valueFormatter: params => {
                if (typeof params.value === 'number') {
                    return params.value.toFixed(1);
                }
                if (isEmpty(params.value)) {
                    return '';
                }
                return params.value;
            },
        });
    }
    if (!isFieldExists('sampleCorrection')) {
        columnsDef.push({
            headerName: 'Привязка',
            field: 'sampleCorrection',
            valueFormatter: params => {
                if (typeof params.value === 'number') {
                    return params.value.toFixed(1);
                }
                if (isEmpty(params.value)) {
                    return '';
                }
                return params.value;
            },
        });
    }
    //TODO Уточнить какое поля соотвествует этому параметру в базе.
    if (!isFieldExists('saturation')) {
        columnsDef.push({
            headerName: 'Насыщение',
            field: 'saturation',
            valueFormatter: params => {
                if (typeof params.value === 'number') {
                    return params.value.toFixed(1);
                }
                if (isEmpty(params.value)) {
                    return '';
                }
                return params.value;
            },
        });
    }

    if (!isFieldExists('lithologyDescr')) {
        columnsDef.push({
            headerName: 'Описание',
            field: 'lithologyDescr',
        });
    }

    coreTypesTitles.forEach(title => {
        columnsDef.push({
            headerName: title,
            field: title,
            valueFormatter: params => {
                if (typeof params.value === 'number') {
                    return title === 'УЭП воды' ? params.value.toFixed(3) : params.value.toFixed(1);
                }
                return params.value;
            },
        });
    });

    return { columnsDef, rowData, coreTypesTitles };
}
